import {
  ApiFeatureCollection,
  ApiFeatureCollectionOverlayMetadataView,
  ApiFeatureCollectionStatus,
  GeometryOverlaySetting,
  PropertyColorRule,
} from 'app/modules/Remote/FeatureCollection';

import {
  FC_PROCESSING_COMPLETE_STATUS,
  PRODUCT_SYSTEM_LINE,
  PRODUCT_SYSTEM_POINT,
  PRODUCT_SYSTEM_POLYGON,
  PRODUCT_USER_LINE,
  PRODUCT_USER_POINT,
  PRODUCT_USER_POLYGON,
  REGRID_FEATURE_COLLECTION_ID,
  VIEW_TYPE_OVERLAY_METADATA,
} from './constants';

const USER_UPLOADED_PRODUCTS = new Set<string>([
  PRODUCT_USER_POINT,
  PRODUCT_USER_POLYGON,
  PRODUCT_USER_LINE,
]);

/** Per-overlay style stored on a share link. Snake_case: it round-trips through the API as-is. */
export interface OverlayStyle {
  color: string;
  default_filled: boolean;
  default_unfilled: boolean;
  property_color_rule?: PropertyColorRule | null;
}

export interface OverlayConfig {
  feature_collection_id: number;
  style: OverlayStyle;
}

/**
 * Filters the raw styleable-attribute list returned by
 * `GET /featureCollections/<id>/styleableAttributes` down to the attributes
 * the user is actually allowed to color the overlay by.
 *
 * Filters applied:
 * 1. Drop attributes with ≤1 distinct value — useless for color splits.
 * 2. For system overlays, restrict to the names the user actually sees on
 *    hover: the keys from `overlay_metadata.hover_properties`, falling back
 *    to ['name'] (the universal final fallback in the hover renderer in
 *    GeometryOverlaysContent.tsx). User-uploaded overlays skip this rule.
 *
 * The fallback to ['name'] naturally disables styling on system overlays
 * whose data doesn't include a `name` attribute (e.g. EPA Superfund,
 * HR Flowlines, PLSS) without needing a hand-maintained list.
 */
export function filterStyleableAttributes(
  fc: ApiFeatureCollection,
  attributes: {name: string; values: string[]}[]
): {name: string; values: string[]}[] {
  const isUserUploaded = USER_UPLOADED_PRODUCTS.has(fc.product);
  let allowlist: string[] | null = null;
  if (!isUserUploaded) {
    const view = fc.views?.find(
      (v): v is ApiFeatureCollectionOverlayMetadataView => v.type === VIEW_TYPE_OVERLAY_METADATA
    );
    const keys = view?.hover_properties?.map((p) => p.key) ?? [];
    allowlist = keys.length > 0 ? keys : ['name'];
  }
  return attributes.filter(
    (a) => a.values.length > 1 && (!allowlist || allowlist.includes(a.name))
  );
}

export const REGRID_OVERLAY: ApiFeatureCollection = {
  bounds: null,
  id: 4153,
  name: 'Parcel Data',
  createdAt: '2021-12-13T20:14:15+00:00',
  updatedAt: '2023-01-30T16:38:42+00:00',
  processingStartDate: null,
  processingEndDate: null,
  isArchived: false,
  product: 'regrid',
  status: null,
  tiles: {
    urls: ['regrid/tiles/{z}/{x}/{y}.mvt'],
    sourceLayer: 'a7bca649f8e586762e0e72a50073d9f4d928c681',
    type: 'vector',
  },
  schema: {
    fields: {},
    geoType: 'POLYGON',
  },
  views: [
    {
      id: 'overlay_metadata',
      type: 'overlay_metadata',
      name: 'Overlay Metadata',
      thumbnail_url: 'https://storage.googleapis.com/upstream-icons/overlays/parcel.png',
    },
  ],
  organizationId: 'system@upstream.tech',
  staticBounds: null,
  type: 'FeatureCollection',
  processingConfig: {
    enrolledLayers: {},
    processingMetadata: {},
  },
  supportedLayersForProduct: {},
} as unknown as ApiFeatureCollection;

export function overlayIsReady(status: ApiFeatureCollectionStatus) {
  return status === null || status === FC_PROCESSING_COMPLETE_STATUS;
}

const OVERLAY_PRODUCTS_OFF_BY_DEFAULT = [
  PRODUCT_SYSTEM_LINE,
  PRODUCT_SYSTEM_POINT,
  PRODUCT_SYSTEM_POLYGON,
];

export type OverlayGeometryType = 'Line' | 'Point' | 'Polygon';

export const OVERLAY_FEATURE_COLLECTION_PRODUCT_TO_GEOMETRY_TYPE: Record<
  string,
  OverlayGeometryType
> = {
  [PRODUCT_SYSTEM_POINT]: 'Point',
  [PRODUCT_SYSTEM_LINE]: 'Line',
  [PRODUCT_SYSTEM_POLYGON]: 'Polygon',
  [PRODUCT_USER_POINT]: 'Point',
  [PRODUCT_USER_LINE]: 'Line',
  [PRODUCT_USER_POLYGON]: 'Polygon',
};

/**
 * Overlay settings for a share link. Unlike the logged-in app there's no saved lens view
 * to merge with: the link carries its own style per overlay, and everything it carries is
 * shown, so each overlay is enabled and treated as fully processed.
 */
export function makeShareLinkOverlaySettings(
  overlayFeatureCollections: Pick<ApiFeatureCollection, 'id' | 'name' | 'product'>[],
  overlayConfigs: OverlayConfig[],
  defaultColors: string[]
): GeometryOverlaySetting[] {
  return overlayFeatureCollections.map((fc, idx) => {
    const style = overlayConfigs.find((c) => c.feature_collection_id === fc.id)?.style;
    return {
      name: fc.name,
      defaultEnabled: true,
      color: style?.color ?? defaultColors[idx % defaultColors.length],
      defaultFilled: style?.default_filled ?? false,
      defaultUnfilled: style?.default_unfilled ?? false,
      status: FC_PROCESSING_COMPLETE_STATUS,
      geometryType: OVERLAY_FEATURE_COLLECTION_PRODUCT_TO_GEOMETRY_TYPE[fc.product] || 'Polygon',
      propertyColorRule: style?.property_color_rule ?? null,
    };
  });
}

/** System defaults from the FC's overlay_metadata view; nulls if absent. */
function getSystemDefaults(fc: ApiFeatureCollection): {
  defaultColor: string | null;
  defaultPropertyColorRule: PropertyColorRule | null;
} {
  const view = fc.views?.find(
    (v): v is ApiFeatureCollectionOverlayMetadataView => v.type === VIEW_TYPE_OVERLAY_METADATA
  );
  return {
    defaultColor: view?.default_color ?? null,
    defaultPropertyColorRule: view?.default_property_color_rule ?? null,
  };
}

/**
 * Merges saved overlay settings from the lens view with the full list of project overlays.
 * Overlays with saved settings use those as-is (user wins; saved propertyColorRule passes
 * through, even when null — that's the single-mode opt-out signal).
 * Overlays not in the lens view get system defaults from overlay_metadata, falling back
 * to the palette color when no default_color is set.
 * Saved settings for overlays no longer in the project are dropped.
 */
export function mergeOverlaySettings(
  savedSettings: Record<string, GeometryOverlaySetting>,
  projectOverlays: ApiFeatureCollection[],
  defaultColors: string[]
): GeometryOverlaySetting[] {
  const overlaysByName: Record<string, ApiFeatureCollection> = {};
  for (const fc of projectOverlays) {
    overlaysByName[fc.name] = fc;
  }

  const result: GeometryOverlaySetting[] = [];

  // Touched overlays: pass saved state through; user wins.
  for (const setting of Object.values(savedSettings)) {
    const fc = overlaysByName[setting.name];
    if (fc) {
      result.push({
        ...setting,
        status: fc.status,
        geometryType: OVERLAY_FEATURE_COLLECTION_PRODUCT_TO_GEOMETRY_TYPE[fc.product] || 'Polygon',
      });
    }
  }

  // Untouched overlays: apply system defaults; no user state to consider.
  let newIdx = 0;
  for (const fc of projectOverlays) {
    if (!(fc.name in savedSettings)) {
      const {defaultColor, defaultPropertyColorRule} = getSystemDefaults(fc);
      result.push({
        name: fc.name,
        color: defaultColor ?? defaultColors[newIdx % defaultColors.length],
        defaultEnabled: !(
          OVERLAY_PRODUCTS_OFF_BY_DEFAULT.includes(fc.product) ||
          fc.id === REGRID_FEATURE_COLLECTION_ID
        ),
        defaultFilled: fc.id === REGRID_FEATURE_COLLECTION_ID,
        status: fc.status,
        geometryType: OVERLAY_FEATURE_COLLECTION_PRODUCT_TO_GEOMETRY_TYPE[fc.product] || 'Polygon',
        propertyColorRule: defaultPropertyColorRule,
      });
      newIdx++;
    }
  }

  return result;
}
